import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Card } from '../ui/Card';
import { useRequest } from '../../hooks/useRequest';
import {
  getUserTasks,
  getUserDailyXp,
  getUserStreakPanel,
  type DailyXpPoint,
  type StreakPanelRange,
  type StreakPanelResponse,
  type StreakPanelTask,
  type UserTask,
} from '../../lib/api';
import { asArray, dateStr } from '../../lib/safe';
import { InfoDotTarget } from '../InfoDot/InfoDotTarget';
import { normalizeGameModeValue, type GameMode } from '../../lib/gameMode';
import { TaskInsightsModal } from './StreakTaskInsightsModal';
import { DashboardMeta, DashboardTitle } from './DashboardTypography';
import { usePostLoginLanguage } from '../../i18n/postLoginLanguage';
import { HABIT_ACHIEVEMENT_UPDATED_EVENT } from '../../lib/habitAchievementEvents';
import { type AvatarProfile } from '../../lib/avatarProfile';
import { resolveAvatarChipColor } from '../../lib/avatarChipPalette';
import { GameModeChip, buildGameModeChip } from '../common/GameModeChip';
import {
  DASHBOARD_SEGMENTED_BUTTON_ACTIVE,
  DASHBOARD_SEGMENTED_BUTTON_BASE,
  DASHBOARD_SEGMENTED_BUTTON_INACTIVE,
  DASHBOARD_SEGMENTED_GROUP_BASE,
} from './segmentedControlStyles';

export const FEATURE_STREAKS_PANEL_V1 = false;

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

type GlowChipProps = {
  glowPrimary: string;
  glowSecondary: string;
  children: ReactNode;
  className?: string;
  innerClassName?: string;
  style?: CSSProperties;
};

function GlowChip({ glowPrimary, glowSecondary, children, className, innerClassName, style }: GlowChipProps) {
  const chipStyle = {
    '--glow-primary': glowPrimary,
    '--glow-secondary': glowSecondary,
    ...style,
  } as CSSProperties;

  return (
    <span className={cx('glow-chip inline-flex', className)} style={chipStyle}>
      <span className={cx('relative z-[1] inline-flex items-center', innerClassName)}>{children}</span>
    </span>
  );
}

interface LegacyStreaksPanelProps {
  userId: string;
}

type LegacyNormalizedDailyXpPoint = DailyXpPoint & { day: string };

type LegacyPanelData = {
  tasks: UserTask[];
  xpSeries: LegacyNormalizedDailyXpPoint[];
};

function legacyBuildRange(daysBack: number) {
  const to = new Date();
  const from = new Date();
  from.setUTCDate(from.getUTCDate() - daysBack);
  return { from: dateStr(from), to: dateStr(to) };
}

function legacyComputeWeeklyXp(series: LegacyNormalizedDailyXpPoint[]): number {
  const sorted = [...series].sort((a, b) => (a.date > b.date ? -1 : 1));
  const recent = sorted.slice(0, 7);
  return recent.reduce((sum, entry) => sum + (entry.xp_day ?? 0), 0);
}

const LEGACY_PILLAR_FILTERS = [
  { label: 'Body', value: 'Body' },
  { label: 'Mind', value: 'Mind' },
  { label: 'Soul', value: 'Soul' },
] as const;

const LEGACY_SCOPE_FILTERS = [
  { label: 'Semana', value: 'week' },
  { label: 'Mes', value: 'month' },
  { label: '3M', value: 'quarter' },
] as const;

export function LegacyStreaksPanel({ userId }: LegacyStreaksPanelProps) {
  const range = useMemo(() => legacyBuildRange(30), []);
  const { data, status } = useRequest<LegacyPanelData>(async () => {
    const [tasks, xp] = await Promise.all([
      getUserTasks(userId),
      getUserDailyXp(userId, range),
    ]);

    const normalizedTasks = asArray<UserTask>(tasks, 'tasks');
    const normalizedSeries = asArray<DailyXpPoint>(xp, 'series').map((row) => {
      const rawDate = (row as any)?.day ?? row.date ?? (row as any)?.created_at ?? (row as any)?.timestamp;
      const day = dateStr(rawDate);
      const fallbackDate = day || row.date || '';
      return {
        ...row,
        day,
        date: fallbackDate,
      } satisfies LegacyNormalizedDailyXpPoint;
    });

    return {
      tasks: normalizedTasks,
      xpSeries: normalizedSeries,
    } satisfies LegacyPanelData;
  }, [userId, range.from, range.to]);

  const weeklyXp = useMemo(() => legacyComputeWeeklyXp(data?.xpSeries ?? []), [data?.xpSeries]);

  const [pillarFilter, setPillarFilter] = useState<(typeof LEGACY_PILLAR_FILTERS)[number]['value']>('Body');
  const [scopeFilter, setScopeFilter] = useState<(typeof LEGACY_SCOPE_FILTERS)[number]['value']>('week');

  const filteredTasks = useMemo(() => {
    if (!data?.tasks) return [];
    return data.tasks.filter((task) => {
      if (!pillarFilter) return true;
      return (task.pillarId ?? '').toString().toLowerCase().includes(pillarFilter.toLowerCase());
    });
  }, [data?.tasks, pillarFilter]);

  return (
    <Card
      title="Panel de Rachas"
      subtitle="Vista read-only"
      rightSlot={
        <span className="inline-flex items-center gap-2 text-xs text-[color:var(--color-slate-400)]">
          <span className="hidden rounded-full border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-[color:var(--color-slate-300)] md:inline-flex">
            Beta
          </span>
          <span aria-hidden>⚡</span>
        </span>
      }
    >
      <div className="flex flex-wrap gap-2">
        {LEGACY_PILLAR_FILTERS.map((filter) => {
          const isActive = pillarFilter === filter.value;
          return (
            <button
              key={filter.value}
              type="button"
              onClick={() => setPillarFilter(filter.value)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60 ${
                isActive
                  ? 'border-indigo-300/60 bg-indigo-300/15 text-indigo-100'
                  : 'border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] text-[color:var(--color-slate-300)] hover:border-[color:var(--color-border-soft)] hover:bg-[color:var(--ib-surface-card-hover)]'
              }`}
            >
              {filter.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        {LEGACY_SCOPE_FILTERS.map((filter) => {
          const isActive = scopeFilter === filter.value;
          return (
            <button
              key={filter.value}
              type="button"
              onClick={() => setScopeFilter(filter.value)}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.18em] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60 ${
                isActive
                  ? 'border-amber-300/60 bg-amber-300/15 text-amber-100'
                  : 'border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] text-[color:var(--color-slate-300)] hover:border-[color:var(--color-border-soft)] hover:bg-[color:var(--ib-surface-card-hover)]'
              }`}
            >
              {filter.label}
            </button>
          );
        })}
      </div>

      {status === 'loading' && <div className="h-56 w-full animate-pulse rounded-ib-md bg-[color:var(--ib-surface-card-active)]" />}

      {status === 'error' && (
        <p className="text-sm text-rose-300">No pudimos cargar tus tareas activas.</p>
      )}

      {status === 'success' && data && (
        <div className="flex flex-col gap-4">
          <p className="text-xs text-[color:var(--color-slate-400)]">
            La API actual aún no expone <code className="rounded bg-[color:var(--ib-surface-card-active)] px-1 py-px text-[10px]">daily_log_raw</code>. Listamos tus tareas activas y calculamos el GP semanal total como referencia.
          </p>

          <div className="rounded-ib-md border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-panel)] p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--color-slate-400)]">GP total últimos 7 días</p>
            <p className="mt-2 text-2xl font-semibold text-[color:var(--color-slate-100)]">{weeklyXp.toLocaleString('es-AR')} GP</p>
            <p className="mt-1 text-xs text-[color:var(--color-slate-400)]">Filtro: {pillarFilter} · Alcance: {scopeFilter === 'week' ? 'Semana' : scopeFilter === 'month' ? 'Mes' : '3 meses'}</p>
          </div>

          <div className="space-y-3">
            {filteredTasks.slice(0, 8).map((task) => (
              <article key={task.id} className="rounded-ib-md border border-[color:var(--color-border-soft)] bg-[color:var(--ib-surface-card)] p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[color:var(--color-slate-100)]">{task.title}</p>
                    <p className="text-xs text-[color:var(--color-slate-400)]">Pilar: {task.pillarId ?? '—'}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[color:var(--color-slate-300)]">
                    <span className="rounded-full border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] px-2 py-1 text-[color:var(--color-slate-100)]">+{task.xp ?? 0} GP</span>
                    <span className="rounded-full border border-[color:var(--color-border-subtle)] bg-[color:var(--ib-surface-card)] px-2 py-1">✓×—</span>
                  </div>
                </div>
              </article>
            ))}

            {filteredTasks.length === 0 && (
              <p className="text-sm text-[color:var(--color-slate-400)]">Sin tareas activas para mostrar con el filtro seleccionado.</p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

type Pillar = 'Body' | 'Mind' | 'Soul';

type Mode = GameMode;

const MODE_TIERS: Record<Mode, number> = {
  Low: 1,
  Chill: 2,
  Flow: 3,
  Evolve: 4,
};

const PILLAR_TABS: Array<{ value: Pillar; label: string; icon: string }> = [
  { value: 'Body', label: 'Body', icon: '🫀' },
  { value: 'Mind', label: 'Mind', icon: '🧠' },
  { value: 'Soul', label: 'Soul', icon: '🏵️' },
];

const RANGE_TABS: Array<{ value: StreakPanelRange; label: string }> = [
  { value: 'week', label: 'Sem' },
  { value: 'month', label: 'Mes' },
  { value: 'qtr', label: '3M' },
];

const PANEL_ENABLED = String(import.meta.env.VITE_SHOW_STREAKS_PANEL ?? 'true').toLowerCase() !== 'false';


function localizeTaskStat(stat: string | undefined, language: 'es' | 'en'): string | undefined {
  if (!stat || language === 'es') {
    return stat;
  }

  const normalized = stat.trim().toLowerCase();
  const map: Record<string, string> = {
    'higiene': 'Hygiene',
    'nutricion': 'Nutrition',
    'nutrición': 'Nutrition',
    'movilidad': 'Mobility',
    'sueno': 'Sleep',
    'sueño': 'Sleep',
    'hidratacion': 'Hydration',
    'hidratación': 'Hydration',
    'tabaco': 'Tobacco',
    'azucar': 'Sugar',
    'azúcar': 'Sugar',
  };

  return map[normalized] ?? stat;
}

interface StreaksPanelProps {
  userId: string;
  gameMode?: string | null;
  weeklyTarget?: number | null;
  avatarProfile?: AvatarProfile | null;
  forceLoadingTasks?: boolean;
  previewData?: StreakPanelResponse | null;
}

type TaskHistory = {
  values: number[];
  labels?: string[];
  totals?: number[];
};

type DisplayTask = {
  id: string;
  name: string;
  stat?: string;
  difficultyLabel?: string | null;
  latestRecalibrationAction?: 'up' | 'keep' | 'down' | null;
  achievementSealVisible?: boolean;
  lifecycleStatus?: string | null;
  weeklyDone: number;
  weeklyGoal: number;
  streakDays: number;
  scopeCount: number;
  scopeXp: number;
  history: TaskHistory;
  highlight?: boolean;
};

const numberFormatter = new Intl.NumberFormat('es-AR');

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '').trim();
  const expanded = normalized.length === 3
    ? normalized.split('').map((chunk) => `${chunk}${chunk}`).join('')
    : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    return `rgba(139, 92, 246, ${alpha})`;
  }
  const int = Number.parseInt(expanded, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function buildStreakModeChipVisual(avatarProfile?: AvatarProfile | null) {
  const chipColor = resolveAvatarChipColor(avatarProfile ?? null);
  return {
    accent: chipColor,
    glowPrimary: hexToRgba(chipColor, 0.26),
    glowSecondary: hexToRgba(chipColor, 0.12),
  };
}

function normalizeMode(mode?: string | null): Mode {
  return normalizeGameModeValue(mode) ?? 'Flow';
}

function computeProgressPercent(done: number, goal: number): number {
  if (goal <= 0) {
    return 0;
  }

  const pct = Math.round((done / goal) * 100);
  if (!Number.isFinite(pct)) {
    return 0;
  }
  return Math.max(0, Math.min(100, pct));
}

function getStatusColor(done: number, goal: number): 'high' | 'mid' | 'low' {
  if (goal <= 0) {
    return 'low';
  }

  if (done >= goal) {
    return 'high';
  }

  if (done / goal >= 0.5) {
    return 'mid';
  }

  return 'low';
}

const MODE_WEEK_OFFSETS: Record<Mode, number> = {
  Low: 2,
  Chill: 2,
  Flow: 1,
  Evolve: 0,
};

function getQuarterHitThreshold(mode: Mode, totalWeeks: number): number {
  const offset = MODE_WEEK_OFFSETS[mode] ?? 0;
  return Math.max(0, totalWeeks - offset);
}

function getRangeMetrics(task: StreakPanelTask | undefined, range: StreakPanelRange) {
  if (!task) {
    return { count: 0, xp: 0, weeks: [] as number[] };
  }

  if (range === 'week') {
    const metrics = task.metrics.week ?? { count: 0, xp: 0 };
    return { count: metrics.count ?? 0, xp: metrics.xp ?? 0, weeks: [] as number[] };
  }

  if (range === 'month') {
    const metrics = task.metrics.month ?? { count: 0, xp: 0, weeks: [] as number[] };
    return { count: metrics.count ?? 0, xp: metrics.xp ?? 0, weeks: metrics.weeks ?? [] };
  }

  const metrics = task.metrics.qtr ?? { count: 0, xp: 0, weeks: [] as number[] };
  return { count: metrics.count ?? 0, xp: metrics.xp ?? 0, weeks: metrics.weeks ?? [] };
}

function buildHistory(task: StreakPanelTask | undefined, range: StreakPanelRange): TaskHistory {
  if (!task || range === 'week') {
    return { values: [] };
  }

  const source = range === 'month' ? task.metrics.month?.weeks ?? [] : task.metrics.qtr?.weeks ?? [];
  const values = source.map((value) => (Number.isFinite(value) ? Number(value) : 0));
  const totals =
    range === 'qtr'
      ? task.metrics.qtr?.weekTotals?.map((value) => (Number.isFinite(value) ? Number(value) : 0))
      : undefined;

  if (values.length === 0) {
    return { values: [] };
  }

  if (range === 'month') {
    const labels = values.map((_, index) => String(index + 1));
    return { values, labels };
  }

  const now = new Date();
  const formatter = new Intl.DateTimeFormat('es-AR', { month: 'short' });
  const labels = values.map((_, index, array) => {
    const monthsAgo = array.length - 1 - index;
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - monthsAgo, 1));
    const raw = formatter.format(date);
    return raw.slice(0, 1).toUpperCase();
  });

  return { values, labels, totals };
}

function normalizeRecalibrationAction(value: string | null | undefined): DisplayTask['latestRecalibrationAction'] {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'up' || normalized === 'keep' || normalized === 'down') {
    return normalized;
  }
  return null;
}

function buildDisplayTask(
  task: StreakPanelTask | undefined,
  fallback: { id: string; name: string; stat?: string; weekDone: number; streakDays: number },
  range: StreakPanelRange,
  weeklyGoal: number,
  highlight?: boolean,
): DisplayTask {
  const weeklyDone = task?.metrics.week?.count ?? fallback.weekDone ?? 0;
  const { count: scopeCount, xp: scopeXp } = getRangeMetrics(task, range);
  const history = buildHistory(task, range);

  return {
    id: fallback.id,
    name: fallback.name,
    stat: fallback.stat,
    difficultyLabel: task?.difficultyLabel ?? null,
    latestRecalibrationAction: normalizeRecalibrationAction(
      (task as { latestRecalibrationAction?: string | null })?.latestRecalibrationAction ??
        (task as { recalibration?: { latest?: { action?: string | null } | null } | null })?.recalibration?.latest?.action,
    ),
    achievementSealVisible: Boolean(task?.achievementSealVisible),
    lifecycleStatus: task?.lifecycleStatus ?? null,
    weeklyDone,
    weeklyGoal,
    streakDays: fallback.streakDays ?? 0,
    scopeCount,
    scopeXp,
    history,
    highlight,
  } satisfies DisplayTask;
}

function getRecalibrationArrow(action: DisplayTask['latestRecalibrationAction']): string {
  if (action === 'up') return '↑';
  if (action === 'down') return '↓';
  if (action === 'keep') return '→';
  return '';
}

function TaskItem({
  item,
  range,
  mode,
  onSelect,
  daysConsecutiveText,
}: {
  item: DisplayTask;
  range: StreakPanelRange;
  mode: Mode;
  onSelect?: (task: DisplayTask) => void;
  daysConsecutiveText: (days: string) => string;
}) {
  const status = getStatusColor(item.weeklyDone, item.weeklyGoal);
  const pct = computeProgressPercent(item.weeklyDone, item.weeklyGoal);
  const showHistory = item.history.values.length > 0;
  const streakDays = Math.max(0, Number.isFinite(item.streakDays) ? Math.floor(item.streakDays) : 0);
  const showFireBadge = streakDays >= 2;
  const showStreakMultiplier = item.highlight && streakDays >= 2;

  const handleClick = () => {
    console.info('[StreaksPanel][TaskItem] Tap on task item', {
      taskId: item.id,
      taskName: item.name,
      streakDays,
      weeklyDone: item.weeklyDone,
      weeklyGoal: item.weeklyGoal,
    });
    onSelect?.(item);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      console.info('[StreaksPanel][TaskItem] Keyboard select on task item', {
        taskId: item.id,
        taskName: item.name,
      });
      onSelect?.(item);
    }
  };

  return (
    <article
      className={cx(
        'ib-card-contour-shadow flex flex-col gap-1.5 rounded-ib-md border border-[color:var(--color-border-soft)] bg-[color:var(--ib-surface-card)] p-2.5 text-[color:var(--color-slate-200)] transition hover:border-violet-300/50 hover:bg-[color:var(--ib-surface-card-hover)] md:gap-2 md:p-3',
        item.highlight && 'border-violet-400/60 bg-violet-400/10 shadow-[0_8px_26px_rgba(99,102,241,0.3)]',
      )}
      aria-label={`Streak ${item.name}, ${item.weeklyDone} of ${item.weeklyGoal} this week, ${streakDays} consecutive days`}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? handleClick : undefined}
      onKeyDown={onSelect ? handleKeyDown : undefined}
    >
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center gap-1.5">
            <div className="truncate text-sm font-medium leading-tight text-[color:var(--color-slate-200)] md:text-base" title={item.name}>
              {item.name}
            </div>
            {item.achievementSealVisible && (
              <span
                className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-[12px] leading-none"
                aria-label="Achieved habit seal"
                title="Achieved habit"
              >
                🏅
              </span>
            )}
          </div>
          {(item.stat || item.difficultyLabel) && (
            <p
              className="truncate text-xs text-[color:var(--color-slate-400)]"
              title={[item.stat, item.difficultyLabel, getRecalibrationArrow(item.latestRecalibrationAction)].filter(Boolean).join(' · ')}
            >
              {item.stat}
              {item.stat && item.difficultyLabel ? ' · ' : ''}
              {item.difficultyLabel}
              {item.difficultyLabel && getRecalibrationArrow(item.latestRecalibrationAction) ? ` ${getRecalibrationArrow(item.latestRecalibrationAction)}` : ''}
            </p>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2 self-start">
          {showFireBadge && (
            <GlowChip
              className="ib-streak-fire-chip"
              glowPrimary="rgba(251, 191, 36, 0.65)"
              glowSecondary="rgba(249, 115, 22, 0.45)"
              innerClassName="ib-streak-fire-chip__inner gap-1 rounded-full border border-amber-400/60 bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-100 shadow-[0_0_12px_rgba(251,191,36,0.25)]"
            >
              <span aria-hidden>🔥</span>
              {showStreakMultiplier ? (
                <>x{numberFormatter.format(streakDays)}</>
              ) : (
                <span className="sr-only">{daysConsecutiveText(numberFormatter.format(streakDays))}</span>
              )}
            </GlowChip>
          )}
          <span aria-hidden className="ml-1 text-lg text-[color:var(--color-slate-400)]">
            ›
          </span>
          <span className="sr-only">Toca para ver más detalles del streak</span>
        </div>
      </div>

      <div
        className={cx(
          'grid gap-2',
          showHistory
            ? 'grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto] items-center gap-x-4'
            : 'grid-cols-1 md:flex md:flex-row md:items-center md:justify-between',
        )}
      >
        <div
          className={cx(
            'flex flex-col gap-1.5 md:min-w-[220px] md:flex-1',
            showHistory && 'row-start-1 self-center',
          )}
        >
          <div className="flex items-center gap-3 -mt-0.5">
            <span
              className={cx(
                'h-2.5 w-2.5 shrink-0 rounded-full',
                status === 'high' && 'bg-emerald-400',
                status === 'mid' && 'bg-amber-400',
                status === 'low' && 'bg-rose-400',
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="h-2.5 overflow-hidden rounded-full" style={{ background: 'var(--gradient-innerbloom-soft)' }}>
                <div
                  className="h-2.5 rounded-full transition-all"
                  style={{ width: `${pct}%`, background: 'var(--gradient-innerbloom)' }}
                />
              </div>
            </div>
            <span className="ml-1 shrink-0 text-xs tabular-nums text-[color:var(--color-slate-200)] md:ml-1.5">
              {item.weeklyDone}/{item.weeklyGoal}
            </span>
          </div>
        </div>

        {showHistory && (
          <div className="flex flex-col items-start gap-1 justify-self-end md:min-w-[72px] md:justify-self-auto">
            <div className="flex items-end gap-1">
              {item.history.values.map((value, index) => {
                const ratio = item.weeklyGoal > 0 ? value / item.weeklyGoal : 0;
                const clamped = Math.max(0, Math.min(ratio, 1.6));
                const height = 12 + clamped * 12;
                const monthlyTarget = range === 'qtr' ? item.weeklyGoal - 0.001 : item.weeklyGoal;
                const completedWeeks = Math.max(0, Math.round(value));
                const totalWeeksCandidate = Math.max(
                  0,
                  Math.round(item.history.totals?.[index] ?? 0),
                );
                const totalWeeks = totalWeeksCandidate > 0 ? totalWeeksCandidate : Math.max(completedWeeks, 4);
                const quarterThreshold = getQuarterHitThreshold(mode, totalWeeks);
                const isQuarterHit = completedWeeks >= quarterThreshold && totalWeeks > 0;
                const isHit =
                  range === 'qtr' ? isQuarterHit : item.weeklyGoal > 0 && value >= monthlyTarget;
                return (
                  <div
                    key={index}
                    className={cx(
                      'w-2 rounded-sm transition-all',
                      isHit ? 'bg-emerald-400/80' : 'bg-slate-500/50',
                    )}
                    style={{ height: `${height}px` }}
                  />
                );
              })}
            </div>
            {item.history.labels && item.history.labels.length > 0 && (
              <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">
                {item.history.labels.map((label, index) => (
                  <span key={index} className="w-2 text-center">
                    {label}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export function StreaksPanel({
  userId,
  gameMode,
  weeklyTarget,
  avatarProfile,
  forceLoadingTasks = false,
  previewData,
}: StreaksPanelProps) {
  const { t, language } = usePostLoginLanguage();
  const [pillar, setPillar] = useState<Pillar>('Body');
  const [range, setRange] = useState<StreakPanelRange>('month');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const normalizedMode = useMemo(() => normalizeMode(gameMode), [gameMode]);
  const tier = useMemo(() => {
    const defaultTier = MODE_TIERS[normalizedMode];
    if (weeklyTarget && weeklyTarget > 0) {
      return weeklyTarget;
    }
    return defaultTier;
  }, [normalizedMode, weeklyTarget]);

  const { data, status, error, reload } = useRequest<StreakPanelResponse>(
    () =>
      getUserStreakPanel(userId, {
        pillar,
        range,
        mode: normalizedMode,
      }),
    [userId, pillar, normalizedMode, range],
    { enabled: PANEL_ENABLED && !previewData },
  );

  useEffect(() => {
    const handler = () => {
      reload();
    };
    window.addEventListener(HABIT_ACHIEVEMENT_UPDATED_EVENT, handler);
    return () => {
      window.removeEventListener(HABIT_ACHIEVEMENT_UPDATED_EVENT, handler);
    };
  }, [reload]);

  if (!PANEL_ENABLED) {
    return null;
  }

  const resolvedData = previewData ?? data;
  const resolvedStatus = previewData ? 'success' : status;
  const tasks = resolvedData?.tasks ?? [];
  const topStreaks = resolvedData?.topStreaks ?? [];

  const tasksById = useMemo(() => new Map(tasks.map((task) => [task.id, task])), [tasks]);

  const streakHighlights = useMemo(
    () => topStreaks.filter((entry) => (entry?.streakDays ?? 0) >= 2),
    [topStreaks],
  );

  const handleSelectTask = useCallback((task: DisplayTask) => {
    setSelectedTaskId((prev) => {
      console.info('[StreaksPanel] handleSelectTask invoked', {
        previousSelectedTaskId: prev,
        nextSelectedTaskId: task.id,
        taskName: task.name,
      });
      return task.id;
    });
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedTaskId(null);
  }, []);

  const streakHighlightMap = useMemo(
    () => new Map(streakHighlights.map((entry) => [entry.id, entry])),
    [streakHighlights],
  );

  const topEntries: DisplayTask[] = useMemo(
    () =>
      streakHighlights.slice(0, 3).map((entry) => {
        const task = tasksById.get(entry.id);
        return buildDisplayTask(
          task,
          {
            id: entry.id,
            name: entry.name,
            stat: entry.stat,
            weekDone: entry.weekDone,
            streakDays: entry.streakDays,
          },
          range,
          tier,
          true,
        );
      }),
    [range, tasksById, tier, streakHighlights],
  );

  const overflowHighlights = useMemo(
    () => streakHighlights.slice(3),
    [streakHighlights],
  );

  const topIds = useMemo(() => new Set(topEntries.map((entry) => entry.id)), [topEntries]);

  const sortedTasks = useMemo(() => {
    const clone = [...tasks];
    clone.sort((a, b) => {
      const xpA = a.metrics.month?.xp ?? 0;
      const xpB = b.metrics.month?.xp ?? 0;
      return xpB - xpA;
    });
    return clone;
  }, [tasks]);

  const displayTasks: DisplayTask[] = useMemo(
    () =>
      [
        ...sortedTasks
          .filter((task) => !topIds.has(task.id))
          .map((task) => {
            const fallback = streakHighlightMap.get(task.id);
            return buildDisplayTask(
              task,
              {
                id: task.id,
                name: fallback?.name ?? task.name,
                stat: fallback?.stat ?? task.stat,
                weekDone: fallback?.weekDone ?? task.weekDone,
                streakDays: fallback?.streakDays ?? task.streakDays,
              },
              range,
              tier,
            );
          }),
        ...overflowHighlights
          .filter((entry) => !tasksById.has(entry.id))
          .map((entry) =>
            buildDisplayTask(
              undefined,
              {
                id: entry.id,
                name: entry.name,
                stat: entry.stat,
                weekDone: entry.weekDone,
                streakDays: entry.streakDays,
              },
              range,
              tier,
            ),
          ),
      ],
    [
      overflowHighlights,
      range,
      sortedTasks,
      streakHighlightMap,
      tasksById,
      tier,
      topIds,
    ],
  );

  const selectedTaskMeta = useMemo(() => {
    if (!selectedTaskId) return null;

    const base = tasksById.get(selectedTaskId);
    if (base) {
      return {
        id: base.id,
        name: base.name,
        stat: base.stat ?? '',
        difficultyLabel: base.difficultyLabel ?? null,
        achievementSealVisible: Boolean(base.achievementSealVisible),
        lifecycleStatus: base.lifecycleStatus ?? null,
        xp: base.metrics.week.xp > 0 ? base.metrics.week.xp : null,
        monthCount: base.metrics.month.count,
        monthXp: base.metrics.month.xp,
      };
    }

    const inDisplay = displayTasks.find((task) => task.id === selectedTaskId);
    if (inDisplay) {
      return {
        id: inDisplay.id,
        name: inDisplay.name,
        stat: inDisplay.stat ?? '',
        difficultyLabel: inDisplay.difficultyLabel ?? null,
        achievementSealVisible: Boolean(inDisplay.achievementSealVisible),
        lifecycleStatus: inDisplay.lifecycleStatus ?? null,
        monthCount: inDisplay.scopeCount,
        monthXp: inDisplay.scopeXp,
      };
    }

    const inTop = topEntries.find((task) => task.id === selectedTaskId);
    if (inTop) {
      return { id: inTop.id, name: inTop.name, stat: inTop.stat ?? '' };
    }

    return null;
  }, [displayTasks, selectedTaskId, tasksById, topEntries]);

  const isLoading = resolvedStatus === 'idle' || resolvedStatus === 'loading';
  const isError = resolvedStatus === 'error';
  const hasContent = !isLoading && !isError;
  const showTasksSkeleton = isLoading || forceLoadingTasks;

  const modeLabel = `${normalizedMode.toUpperCase()} · ${tier}×/WEEK`;
  const rangeTabs = RANGE_TABS.map((tab) => ({
    ...tab,
    label:
      tab.value === 'week'
        ? t('dashboard.streaks.week')
        : tab.value === 'month'
          ? t('dashboard.streaks.month')
          : tab.label,
  }));
  const daysConsecutiveText = (days: string) => t('dashboard.streaks.daysConsecutiveSr', { days });
  const modeChipVisual = buildStreakModeChipVisual(avatarProfile);
  const modeChipBase = buildGameModeChip(normalizedMode, { avatarProfile });
  const modeChipStyle = { ...modeChipBase.style, '--ib-chip-accent': modeChipVisual.accent } as CSSProperties;

  return (
    <>
      <Card
        title="Streaks"
        bodyClassName="gap-5 p-3 text-[color:var(--color-slate-100)] md:p-4"
        className="text-sm leading-relaxed"
        rightSlot={
          <InfoDotTarget id="streaksGuide" placement="left" className="flex items-center gap-2">
            <GameModeChip
              label={modeLabel}
              animate={modeChipBase.animate}
              style={modeChipStyle}
              className="ib-streak-mode-chip"
            />
          </InfoDotTarget>
        }
      >
        <div className="flex flex-col gap-4">
          <div data-demo-anchor="streaks-top" className="space-y-4">
            <div className="flex items-center justify-center">
              <div className={cx(DASHBOARD_SEGMENTED_GROUP_BASE, 'max-w-[320px]')}>
                {PILLAR_TABS.map((tab) => {
                  const isActive = pillar === tab.value;
                  return (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => setPillar(tab.value)}
                      className={cx(
                        DASHBOARD_SEGMENTED_BUTTON_BASE,
                        'leading-none',
                        isActive
                          ? DASHBOARD_SEGMENTED_BUTTON_ACTIVE
                          : DASHBOARD_SEGMENTED_BUTTON_INACTIVE,
                      )}
                      aria-pressed={isActive}
                    >
                      <span aria-hidden>{tab.icon}</span>
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

          {isError && (
            <p className="text-sm text-rose-300">
              No pudimos cargar tus rachas activas
              {error?.message ? `: ${error.message}` : '.'}
            </p>
          )}

          {isLoading ? (
            <div className="grid grid-cols-1 gap-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`top-skeleton-${index}`} className="h-24 animate-pulse rounded-ib-md border border-[color:var(--color-border-soft)] bg-[color:var(--ib-surface-card)]" />
              ))}
            </div>
          ) : (
            hasContent && (
              <section className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <DashboardTitle level="h2" as="h4" className="text-[color:var(--color-text)]">
                    Top streaks
                  </DashboardTitle>
                  <DashboardMeta as="span" className="text-[color:var(--color-text)]">{t('dashboard.streaks.consecutiveDays')}</DashboardMeta>
                </div>
                {topEntries.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3">
                    {topEntries.map((entry) => (
                      <TaskItem
                        key={entry.id}
                        item={{ ...entry, stat: localizeTaskStat(entry.stat, language) }}
                        range={range}
                        mode={normalizedMode}
                        onSelect={handleSelectTask}
                        daysConsecutiveText={daysConsecutiveText}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[color:var(--color-slate-400)]">{t('dashboard.streaks.noneHighlighted')}</p>
                )}
              </section>
            )
          )}
          </div>

          <div data-demo-anchor="streaks-bottom" className="space-y-4">
            <InfoDotTarget
              id="scopesGuide"
              placement="top"
              className="flex flex-wrap items-center justify-center gap-2"
            >
              <div className={cx(DASHBOARD_SEGMENTED_GROUP_BASE, 'max-w-[220px]')}>
                {rangeTabs.map((tab) => {
                  const isActive = range === tab.value;
                  return (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => setRange(tab.value)}
                      className={cx(
                        DASHBOARD_SEGMENTED_BUTTON_BASE,
                        'leading-none',
                        isActive
                          ? DASHBOARD_SEGMENTED_BUTTON_ACTIVE
                          : DASHBOARD_SEGMENTED_BUTTON_INACTIVE,
                      )}
                      aria-pressed={isActive}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </InfoDotTarget>

            {showTasksSkeleton ? (
              <div className="grid grid-cols-1 gap-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={`task-skeleton-${index}`} className="h-24 animate-pulse rounded-ib-md border border-[color:var(--color-border-soft)] bg-[color:var(--ib-surface-card)]" />
                ))}
              </div>
            ) : (
              hasContent && (
                <section className="space-y-3">
                  <DashboardTitle level="h2" as="h4" className="text-[color:var(--color-text)]">
                    {t('dashboard.streaks.allTasks')}
                  </DashboardTitle>
                  {displayTasks.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3">
                      {displayTasks.map((task) => (
                        <TaskItem
                          key={task.id}
                          item={{ ...task, stat: localizeTaskStat(task.stat, language) }}
                          range={range}
                          mode={normalizedMode}
                          onSelect={handleSelectTask}
                          daysConsecutiveText={daysConsecutiveText}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[color:var(--color-slate-400)]">
                      {t('dashboard.streaks.noneForFilter')}
                    </p>
                  )}
                </section>
              )
            )}
          </div>
        </div>
      </Card>
      <TaskInsightsModal
        taskId={selectedTaskId}
        weeklyGoal={tier}
        mode={normalizedMode}
        range={range}
        fallbackTask={selectedTaskMeta}
        onClose={handleCloseModal}
      />
    </>
  );
}
